import { reactive } from 'vue'

import {
  resolveRolling,
  type ChartSeries,
  type InstantRangeValue,
  type RollingSpan,
} from '@hantera/design-system'
import type egresses from '@/backend/egresses'
import type ingresses from '@/backend/ingresses'
import type jobs from '@/backend/jobs'
import type {
  EgressHostStatistics,
  EgressKeyStatistics,
  EgressStatisticBucket,
} from '@/backend/egresses.types'
import type { IngressIdStatistics, IngressStatisticBucket } from '@/backend/ingresses.types'
import type { JobQueueStatistics, JobQueueStatisticBucket } from '@/backend/jobs.types'
import {
  bucketLabels,
  bucketTimes,
  calculateBucketMinutes,
  extremeOf,
  foldSeries,
  peakOf,
  sumOf,
  weightedAverage,
} from './bucketing'
import type { DateRange } from './traces-view-context'
import type {
  EgressRow,
  HostRow,
  IngressRow,
  JobDefinitionRow,
  QueueRow,
  StatisticsSections,
  StatisticsViewContext,
} from './statistics-view-context'

export interface StatisticsServices {
  egressesService: ReturnType<typeof egresses>
  ingressesService: ReturnType<typeof ingresses>
  jobsService: ReturnType<typeof jobs>
}

/** A job definition's per-minute buckets, as the API returns them. */
interface JobDefinitionStats {
  jobDefinitionId: string
  pending: number
  buckets: any[]
}

/**
 * A monitoring view opens live. An hour of one-minute buckets renders at full resolution, whereas
 * the day this defaulted to folds into fifteen-minute buckets and flattens the spike the operator
 * came to look at. A fixed span is one click away in the range picker.
 */
const DEFAULT_ROLLING: RollingSpan = { amount: 60, unit: 'minutes' }

export class StatisticsViewContextImpl implements StatisticsViewContext {
  readonly loadState = reactive({
    isLoading: false,
    error: null as string | null,
    lastRefreshedAt: null as Date | null,
  })

  readonly dateRange = reactive<DateRange>(resolveRolling(DEFAULT_ROLLING))

  private readonly rollingSpan = reactive<{ value: RollingSpan | null }>({
    value: { ...DEFAULT_ROLLING },
  })

  private readonly ingressStats = reactive<IngressIdStatistics[]>([])
  private readonly egressStats = reactive<EgressKeyStatistics[]>([])
  private readonly hostStats = reactive<EgressHostStatistics[]>([])
  private readonly queueStats = reactive<JobQueueStatistics[]>([])
  private readonly jobStats = reactive<JobDefinitionStats[]>([])

  private readonly egressesService: ReturnType<typeof egresses>
  private readonly ingressesService: ReturnType<typeof ingresses>
  private readonly jobsService: ReturnType<typeof jobs>

  constructor(
    services: StatisticsServices,
    readonly sections: StatisticsSections,
  ) {
    this.egressesService = services.egressesService
    this.ingressesService = services.ingressesService
    this.jobsService = services.jobsService
  }

  get rolling(): RollingSpan | null {
    return this.rollingSpan.value
  }

  private get bucketMinutes(): number {
    return calculateBucketMinutes(this.dateRange.to.getTime() - this.dateRange.from.getTime())
  }

  private get times(): string[] {
    return bucketTimes(this.dateRange, this.bucketMinutes)
  }

  /**
   * Builds one chart series per group.
   *
   * Series that are entirely empty are dropped: an egress with no calls in the window would
   * otherwise occupy a legend slot and a colour while drawing nothing.
   */
  private toSeries<TGroup, TBucket extends { bucketTime: Date }>(
    groups: TGroup[],
    label: (group: TGroup) => string,
    buckets: (group: TGroup) => TBucket[],
    combine: (rows: TBucket[]) => number | null,
  ): ChartSeries[] {
    const minutes = this.bucketMinutes
    const times = this.times
    const labels = bucketLabels(times, minutes)

    return groups
      .map((group) => ({
        label: label(group),
        points: foldSeries(buckets(group), times, labels, minutes, combine),
      }))
      .filter((series) => series.points.some((p) => p.value != null && p.value !== 0))
  }

  get ingressRequestSeries(): ChartSeries[] {
    return this.toSeries(
      this.ingressStats,
      (i) => i.ingressId,
      (i) => i.buckets,
      (rows) => (rows.length ? sumOf(rows, (r: IngressStatisticBucket) => r.requests) : null),
    )
  }

  get ingressLatencySeries(): ChartSeries[] {
    return this.toSeries(
      this.ingressStats,
      (i) => i.ingressId,
      (i) => i.buckets,
      (rows) =>
        weightedAverage(
          rows,
          (r: IngressStatisticBucket) => r.p95DurationMs,
          (r) => r.requests,
        ),
    )
  }

  get egressCallSeries(): ChartSeries[] {
    return this.toSeries(
      this.egressStats,
      (e) => e.egressKey,
      (e) => e.buckets,
      (rows) => (rows.length ? sumOf(rows, (r: EgressStatisticBucket) => r.total) : null),
    )
  }

  get egressLatencySeries(): ChartSeries[] {
    return this.toSeries(
      this.egressStats,
      (e) => e.egressKey,
      (e) => e.buckets,
      (rows) =>
        weightedAverage(
          rows,
          (r: EgressStatisticBucket) => r.p95DurationMs,
          (r) => r.total,
        ),
    )
  }

  get hostCallSeries(): ChartSeries[] {
    return this.toSeries(
      this.hostStats,
      (h) => h.host,
      (h) => h.buckets,
      (rows) => (rows.length ? sumOf(rows, (r: EgressStatisticBucket) => r.total) : null),
    )
  }

  get hostLatencySeries(): ChartSeries[] {
    return this.toSeries(
      this.hostStats,
      (h) => h.host,
      (h) => h.buckets,
      (rows) =>
        weightedAverage(
          rows,
          (r: EgressStatisticBucket) => r.p95DurationMs,
          (r) => r.total,
        ),
    )
  }

  get jobOutcomeSeries(): ChartSeries[] {
    return this.toSeries(
      this.jobStats,
      (d) => d.jobDefinitionId,
      (d) => d.buckets,
      (rows) => (rows.length ? sumOf(rows, (r: any) => Number(r.successful ?? 0)) : null),
    )
  }

  get jobLatencySeries(): ChartSeries[] {
    return this.toSeries(
      this.jobStats,
      (d) => d.jobDefinitionId,
      (d) => d.buckets,
      (rows) =>
        weightedAverage(
          rows,
          (r: any) => (r.p95Execution == null ? null : Number(r.p95Execution)),
          (r: any) => Number(r.successful ?? 0) + Number(r.failed ?? 0),
        ),
    )
  }

  /**
   * Depth is a gauge, so a display bucket takes the peak rather than a sum: adding the depth of
   * fifteen consecutive minutes would report a backlog fifteen times its real size.
   */
  get queueDepthSeries(): ChartSeries[] {
    return this.toSeries(
      this.queueStats,
      (q) => q.queue,
      (q) => q.buckets,
      (rows) => peakOf(rows, (r: JobQueueStatisticBucket) => r.readyDepth),
    )
  }

  get ingressRows(): IngressRow[] {
    return this.ingressStats
      .map((ingress) => ({
        ingressId: ingress.ingressId,
        requests: sumOf(ingress.buckets, (b) => b.requests),
        clientErrors: sumOf(ingress.buckets, (b) => b.clientErrors),
        serverErrors: sumOf(ingress.buckets, (b) => b.serverErrors),
        p95DurationMs: weightedAverage(
          ingress.buckets,
          (b) => b.p95DurationMs,
          (b) => b.requests,
        ),
        maxDurationMs: extremeOf(ingress.buckets, (b) => b.maxDurationMs, Math.max),
      }))
      .sort((a, b) => a.ingressId.localeCompare(b.ingressId))
  }

  get egressRows(): EgressRow[] {
    return this.egressStats
      .map((egress) => ({
        egressKey: egress.egressKey,
        total: sumOf(egress.buckets, (b) => b.total),
        failed: sumOf(egress.buckets, (b) => b.failed),
        p95DurationMs: weightedAverage(
          egress.buckets,
          (b) => b.p95DurationMs,
          (b) => b.total,
        ),
        maxDurationMs: extremeOf(egress.buckets, (b) => b.maxDurationMs, Math.max),
      }))
      .sort((a, b) => a.egressKey.localeCompare(b.egressKey))
  }

  /**
   * Slowest first, and deliberately not by name.
   *
   * Every other table here sorts by name because the operator arrives knowing which resource they
   * care about. Here they do not: the point is to surface the partner that is misbehaving without
   * being asked for it.
   */
  get hostRows(): HostRow[] {
    return this.hostStats
      .map((host) => ({
        host: host.host,
        transport: host.transport,
        total: sumOf(host.buckets, (b) => b.total),
        failed: sumOf(host.buckets, (b) => b.failed),
        p95DurationMs: weightedAverage(
          host.buckets,
          (b) => b.p95DurationMs,
          (b) => b.total,
        ),
        maxDurationMs: extremeOf(host.buckets, (b) => b.maxDurationMs, Math.max),
      }))
      .sort((a, b) => (b.p95DurationMs ?? -1) - (a.p95DurationMs ?? -1))
  }

  get jobRows(): JobDefinitionRow[] {
    return this.jobStats
      .map((definition) => ({
        jobDefinitionId: definition.jobDefinitionId,
        pending: Number(definition.pending ?? 0),
        successful: sumOf(definition.buckets, (b: any) => Number(b.successful ?? 0)),
        failed: sumOf(definition.buckets, (b: any) => Number(b.failed ?? 0)),
        p95ExecutionMs: weightedAverage(
          definition.buckets,
          (b: any) => (b.p95Execution == null ? null : Number(b.p95Execution)),
          (b: any) => Number(b.successful ?? 0) + Number(b.failed ?? 0),
        ),
        maxExecutionMs: extremeOf(
          definition.buckets,
          (b: any) => (b.maxExecution == null ? null : Number(b.maxExecution)),
          Math.max,
        ),
      }))
      .sort((a, b) => a.jobDefinitionId.localeCompare(b.jobDefinitionId))
  }

  /**
   * The gauges come from the most recent bucket, not the whole range: "412 waiting" is a fact about
   * now, and averaging it over a day would hide the spike that matters. The counters do span the
   * range, since throughput is only meaningful over an interval.
   */
  get queueRows(): QueueRow[] {
    return this.queueStats
      .map((queue) => {
        const latest = queue.buckets.reduce<JobQueueStatisticBucket | null>(
          (newest, bucket) =>
            newest == null || new Date(bucket.bucketTime) > new Date(newest.bucketTime)
              ? bucket
              : newest,
          null,
        )

        const workers = latest?.workers ?? 0

        return {
          queue: queue.queue,
          readyDepth: latest?.readyDepth ?? 0,
          busyWorkers: latest?.busyWorkers ?? 0,
          workers,
          saturation: workers > 0 ? (latest?.busyWorkers ?? 0) / workers : null,
          completed: sumOf(queue.buckets, (b) => b.completed),
          failed: sumOf(queue.buckets, (b) => b.failed),
        }
      })
      .sort((a, b) => a.queue.localeCompare(b.queue))
  }

  /**
   * Loads every readable section.
   *
   * Failures are collected rather than aborting the refresh: one section 403ing or erroring must not
   * blank the others, since the operator came here to compare them.
   */
  async refresh(): Promise<void> {
    this.loadState.isLoading = true
    this.loadState.error = null


    const from = this.dateRange.from
    const to = this.dateRange.to
    const failures: string[] = []

    const load = async (name: string, run: () => Promise<void>) => {
      try {
        await run()
      } catch (e: any) {
        console.error(`Failed to load ${name} statistics:`, e)
        failures.push(name)
      }
    }

    const loads: Promise<void>[] = []

    if (this.sections.ingress) {
      loads.push(
        load('ingress', async () => {
          const result = await this.ingressesService.getStatistics({ from, to })
          this.ingressStats.splice(0, this.ingressStats.length, ...result)
        }),
      )
    }

    if (this.sections.egress) {
      loads.push(
        load('egress', async () => {
          const result = await this.egressesService.getStatistics({ from, to })
          this.egressStats.splice(0, this.egressStats.length, ...result)
        }),
        load('host', async () => {
          const result = await this.egressesService.getHostStatistics({ from, to })
          this.hostStats.splice(0, this.hostStats.length, ...result)
        }),
      )
    }

    if (this.sections.jobs) {
      loads.push(
        load('job', async () => {
          const result = await this.jobsService.getStatistics({ from, to })
          this.jobStats.splice(0, this.jobStats.length, ...((result.definitions ?? []) as any))
        }),
        load('queue', async () => {
          const result = await this.jobsService.getQueueStatistics({ from, to })
          this.queueStats.splice(0, this.queueStats.length, ...result)
        }),
      )
    }

    await Promise.all(loads)

    this.loadState.isLoading = false
    this.loadState.lastRefreshedAt = new Date()

    if (failures.length > 0) {
      this.loadState.error = `Failed to load: ${failures.join(', ')}`
    }
  }


  /**
   * Slides a rolling window forward before refreshing, so the charts gain a bucket on the right and
   * drop one on the left rather than compressing against a frozen end.
   *
   * With a fixed range this degrades to a plain refresh, which is what lets one timer serve both
   * modes without the caller branching.
   */
  async tick(): Promise<void> {
    const span = this.rollingSpan.value
    if (span) {
      const { from, to } = resolveRolling(span)
      this.dateRange.from = from
      this.dateRange.to = to
    }

    await this.refresh()
  }

  setRange(range: InstantRangeValue): void {
    this.rollingSpan.value = range.rolling ? { ...range.rolling } : null

    // A range extending into the future has no data in it and would pad the axis with empty buckets,
    // pushing the interesting part off the left of the chart.
    const now = new Date()
    let next = range.to > now ? { from: range.from, to: now } : { from: range.from, to: range.to }
    if (next.from > next.to) {
      next = { from: next.to, to: next.from }
    }

    this.dateRange.from = next.from
    this.dateRange.to = next.to
  }

  /**
   * Upserts a live bucket into a group's series.
   *
   * Buckets outside the range are dropped rather than appended: the axis is derived from the range,
   * so a bucket beyond it would be invisible while still counting toward the totals beside it.
   */
  private upsert<TGroup extends { buckets: TBucket[] }, TBucket extends { bucketTime: Date }>(
    groups: TGroup[],
    matches: (group: TGroup) => boolean,
    create: () => TGroup,
    bucket: TBucket,
  ): void {
    const time = new Date(bucket.bucketTime)
    if (time < this.dateRange.from || time > this.dateRange.to) {
      return
    }

    let group = groups.find(matches)
    if (!group) {
      group = create()
      groups.push(group)
    }

    const key = time.toISOString()
    const index = group.buckets.findIndex((b) => new Date(b.bucketTime).toISOString() === key)

    if (index >= 0) {
      group.buckets[index] = bucket
    } else {
      group.buckets.push(bucket)
    }
  }

  handleIngressEvent(data: any): void {
    this.upsert(
      this.ingressStats,
      (i) => i.ingressId === data.ingressId,
      () => ({ ingressId: data.ingressId, componentId: null, buckets: [] }),
      {
        bucketTime: new Date(data.bucketTimestamp),
        transport: data.transport ?? 'http',
        requests: data.requests ?? data.total ?? 0,
        successes: data.successes ?? 0,
        clientErrors: data.clientErrors ?? 0,
        serverErrors: data.serverErrors ?? 0,
        minDurationMs: data.minDurationMs ?? null,
        maxDurationMs: data.maxDurationMs ?? null,
        avgDurationMs: data.avgDurationMs ?? null,
        p95DurationMs: data.p95DurationMs ?? null,
      },
    )
  }

  handleEgressEvent(data: any): void {
    this.upsert(
      this.egressStats,
      (e) => e.egressKey === data.egressKey,
      () => ({ egressKey: data.egressKey, componentId: null, idempotent: false, buckets: [] }),
      {
        bucketTime: new Date(data.bucketTimestamp),
        total: data.total,
        successful: data.successful,
        failed: data.failed,
        minDurationMs: data.minDurationMs ?? null,
        maxDurationMs: data.maxDurationMs ?? null,
        avgDurationMs: data.avgDurationMs ?? null,
        p95DurationMs: data.p95DurationMs ?? null,
      },
    )
  }

  handleHostEvent(data: any): void {
    this.upsert(
      this.hostStats,
      (h) => h.host === data.host && h.transport === data.transport,
      () => ({ host: data.host, transport: data.transport, buckets: [] }),
      {
        bucketTime: new Date(data.bucketTimestamp),
        total: data.total,
        successful: data.successful,
        failed: data.failed,
        minDurationMs: data.minDurationMs ?? null,
        maxDurationMs: data.maxDurationMs ?? null,
        avgDurationMs: data.avgDurationMs ?? null,
        p95DurationMs: data.p95DurationMs ?? null,
      },
    )
  }

  handleJobEvent(data: any): void {
    this.upsert(
      this.jobStats,
      (d) => d.jobDefinitionId === data.jobDefinitionId,
      () => ({ jobDefinitionId: data.jobDefinitionId, pending: 0, buckets: [] }),
      {
        bucketTime: new Date(data.bucketTimestamp),
        scheduled: data.scheduled ?? 0,
        successful: data.successful ?? 0,
        failed: data.failed ?? 0,
        minExecution: data.minExecutionMs ?? null,
        maxExecution: data.maxExecutionMs ?? null,
        avgExecution: data.avgExecutionMs ?? null,
        p95Execution: data.p95ExecutionMs ?? null,
      } as any,
    )
  }

  handleQueueEvent(data: any): void {
    this.upsert(
      this.queueStats,
      (q) => q.queue === data.queue,
      () => ({ queue: data.queue, buckets: [] }),
      {
        bucketTime: new Date(data.bucketTimestamp),
        readyDepth: data.readyDepth ?? 0,
        busyWorkers: data.busyWorkers ?? 0,
        workers: data.workers ?? 0,
        scheduled: data.scheduled ?? 0,
        completed: data.completed ?? 0,
        failed: data.failed ?? 0,
      },
    )
  }
}
